import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGetInvestmentPlanQuery } from '../../features/investmentPlans/investmentPlansApi';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { DataRow } from '../../components/ui/MobileCard';
import PlanPhoto from '../../components/plans/PlanPhoto';
import PlanErrorState from '../../components/plans/PlanErrorState';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatDuration, formatRoi } from '../../lib/formatPlan';

/**
 * GET /investor/investment-plans/{uuid} — dedicated screen (not just a
 * modal reusing list data) so a 404 for a stale/bad uuid gets its own
 * "Plan not found" message instead of a generic crash, and so the URL is
 * shareable/deep-linkable. Browse-only: no Edit/Delete here regardless of
 * the logged-in investor's role — those stay admin-dashboard-only. No
 * Subscribe action either, since the backend doesn't have one yet.
 */
export default function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: plan,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetInvestmentPlanQuery(id, { skip: !id });

  const notFound = isError && error?.status === 404;

  return (
    <div className="space-y-6">
      <Link to="/plans" className="text-sm text-accent hover:underline">
        ← Back to plans
      </Link>

      {isLoading && (
        <Card className="space-y-4">
          <Skeleton className="h-56 w-full rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-3 border-t border-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {notFound && (
        <Card>
          <div className="py-10 text-center space-y-3">
            <p className="text-sm text-foreground-muted">Plan not found.</p>
            <Button size="sm" variant="secondary" onClick={() => navigate('/plans')}>
              Back to catalog
            </Button>
          </div>
        </Card>
      )}

      {isError && !notFound && (
        <Card>
          <PlanErrorState error={error} onRetry={refetch} />
        </Card>
      )}

      {!isLoading && !isError && plan && (
        <Card className="space-y-4">
          <PlanPhoto src={plan.photo_url} alt={plan.name} className="h-56 w-full rounded-xl" />

          <div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-xl font-semibold text-foreground">{plan.name}</h1>
              <Badge tone="success" className="shrink-0">
                {formatRoi(plan.roi_percentage)} ROI
              </Badge>
            </div>
            {plan.description && <p className="text-sm text-foreground-muted mt-2 whitespace-pre-line">{plan.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3 border-t border-border">
            <DataRow label="Minimum" value={formatCurrency(plan.minimum_amount)} />
            <DataRow label="Maximum" value={plan.maximum_amount != null ? formatCurrency(plan.maximum_amount) : 'No cap'} />
            <DataRow label="ROI" value={formatRoi(plan.roi_percentage)} />
            <DataRow label="Duration" value={formatDuration(plan.duration_in_days)} />
          </div>

          <div className="pt-3 border-t border-border">
            <Button variant="secondary" size="sm" disabled title="Investor subscriptions aren't live yet.">
              Subscribe — coming soon
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
